import { useState, useEffect } from "react";
import {
  getClientReviews,
  getTenantById,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";

export interface CitationData {
  citations: string[];
  search_results: SearchResult[];
  content: {
    reviews: Review[];
    summary_table: SummaryTableItem[];
    categorical_summary: {
      service_quality_and_support: string;
      product_performance: string;
      response_time: string;
      communication_and_documentation: string;
    };
    limitations_and_inferences: string;
  };
}

export interface SearchResult {
  title: string;
  url: string;
  date: string | null;
  last_updated: string | null;
  author?: string;
  tags?: string[];
  source?: string;
  content?: string;
  rating?: number;
}

export interface Review {
  date_of_review: string | null;
  star_rating: string | null;
  review_text: string | null;
  summary: string;
  source: string;
  reviewer_designation: string;
  category: string;
}

export interface SummaryTableItem {
  date: string | null;
  rating: string | null;
  reviewer_role: string;
  source: string;
  quoted_text: string | null;
  category: string;
}

interface ClientProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

export const ClientReviews: React.FC<ClientProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<CitationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      try {
        const response = await getClientReviews(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (error) {
        console.error("Error fetching client reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId, loading]);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };
  const getNumericRating = (star_rating: string | null) => {
    if (!star_rating) return null;
    const match = star_rating.match(/^(\d+)/); // Extract first number
    return match ? parseInt(match[1], 10) : null;
  };

 const downloadPDF = async () => {
  setDownloadingPdf(true);
  try {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginX = 20;
    const marginY = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - marginX * 2;

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Client Reviews", marginX, marginY);

    // Line under header
    doc.setDrawColor(190);
    doc.setLineWidth(0.5);
    doc.line(marginX, marginY + 3, pageWidth - marginX, marginY + 3);

    // Metadata
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, marginX, marginY + 12);
    doc.text(`Tenant: ${tenant?.name || "N/A"}`, marginX, marginY + 19);

    // Summary
    const positiveReviews = data?.content.reviews.filter((r) => {
      const rating = getNumericRating(r.star_rating);
      return rating !== null && rating >= 3;
    }).length || 0;
    const totalReviews = data?.content.reviews.length || 0;
    const positivePercentage = totalReviews > 0 ? (positiveReviews / totalReviews) * 100 : 0;

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40);
    doc.text("Review Summary", marginX, marginY + 30);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Reviews: ${totalReviews}`, marginX, marginY + 38);
    doc.text(`Positive Reviews: ${Math.round(positivePercentage)}%`, marginX, marginY + 45);

    let currentY = marginY + 55;

    // Reviews
    const reviewsToExport = activeCategory
      ? data?.content.reviews.filter((r) => r.category.includes(activeCategory)) || []
      : data?.content.reviews || [];

    reviewsToExport.forEach((review) => {
      if (currentY > pageHeight - 50) {
        doc.addPage();
        currentY = marginY;
      }

      // Date
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100);
      const dateText = review.date_of_review
        ? new Date(review.date_of_review).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "No date";
      doc.text(dateText, marginX, currentY);
      currentY += 6;

      // Rating
      const rating = getNumericRating(review.star_rating);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(rating && rating <= 2 ? 200 : 80);
      doc.text(`Rating: ${review.star_rating || "No rating"}`, marginX, currentY);
      currentY += 6;

      // Source and Category
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`${review.source} • ${review.category}`, marginX, currentY);
      currentY += 8;

      // Review text
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60);
      const reviewText = review.review_text || review.summary;
      const lines = doc.splitTextToSize(reviewText, contentWidth);
      doc.text(lines, marginX, currentY);
      currentY += lines.length * 6;

      currentY += 12;
    });

    // Add watermark and footer to all pages
    if (tenant?.logoUrl) {
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = tenant.logoUrl as string;
        });

        const watermarkWidth = 90;
        const watermarkHeight = (img.height / img.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          
          // Add watermark
          doc.saveGraphicsState();
          doc.setGState(new (doc.GState as any)({ opacity: 0.07 }));
          doc.addImage(img, "PNG", xPos, yPos, watermarkWidth, watermarkHeight);
          doc.restoreGraphicsState();
          
          // Add footer
          doc.setFontSize(8);
          doc.setTextColor(130);
          doc.text(`Page ${i} of ${totalPages} | Confidential`, pageWidth / 2, pageHeight - 10, { align: "center" });
        }
      } catch (error) {
        console.warn("Failed to load watermark:", error);
      }
    }

    doc.save(`client-reviews-${tenantId}-${new Date().getTime()}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    setDownloadingPdf(false);
  }
};


  const downloadWord = async () => {
    setDownloadingWord(true);
    try {
      const {
        Document,
        Paragraph,
        TextRun,
        HeadingLevel,
        AlignmentType,
        ImageRun,
        Packer,
        convertInchesToTwip,
        PageOrientation,
        Footer,
        Table,
        TableRow,
        TableCell,
        WidthType,
      } = await import("docx");

      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          const response = await fetch(tenant?.logoUrl);
          const blob = await response.blob();

          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const binaryString = window.atob(base64);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          logoImageData = { data: bytes, mimeType: getMimeType(blob.type) };
        } catch (error) {
          console.warn("Failed to load logo:", error);
        }
      }

      const reviewsToExport = activeCategory
        ? data?.content.reviews.filter((r) =>
            r.category.includes(activeCategory)
          ) || []
        : data?.content.reviews || [];

      const doc = new Document({
        creator: "Your Application",
        title: "Client Reviews",
        description: "Client reviews document",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(1),
                  right: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(1),
                  left: convertInchesToTwip(0.75),
                },
                size: {
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            children: [
              new Paragraph({
                text: "Client Reviews",
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: `Generated on: ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                text: `Tenant: ${tenant?.name || "N/A"}`,
                alignment: AlignmentType.CENTER,
              }),

              // Summary
              new Paragraph({
                text: "Review Summary",
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 240, after: 120 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• Total Reviews: ${
                      data?.content.reviews.length || 0
                    }`,
                    size: 10,
                    color: "444444",
                  }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `• Positive Reviews: ${Math.round(
                      ((data?.content.reviews.filter((r) => {
                        const rating = getNumericRating(r.star_rating);
                        return rating !== null && rating >= 3;
                      }).length || 0) /
                        (data?.content.reviews.length || 1)) *
                        100
                    )}%`,
                    size: 10,
                    color: "444444",
                  }),
                ],
                spacing: { after: 200 },
              }),

              // Reviews Table
              new Table({
                rows: reviewsToExport.map((review) => {
                  const dateText = review.date_of_review
                    ? new Date(review.date_of_review).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : "No date";

                  return new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: dateText,
                                bold: true,
                                size: 10,
                                color: "666666",
                              }),
                              new TextRun({
                                text: `\n${review.star_rating || "No rating"}`,
                                size: 9,
                                color: review.star_rating
                                  ? (() => {
                                      const numericRating = getNumericRating(
                                        review.star_rating
                                      );
                                      return numericRating !== null &&
                                        numericRating <= 2
                                        ? "CC0000"
                                        : "666666";
                                    })()
                                  : "666666",
                              }),
                            ],
                          }),
                        ],
                        width: { size: 25, type: WidthType.PERCENTAGE },
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: review.review_text || review.summary,
                                size: 10,
                                color: "333333",
                              }),
                            ],
                          }),
                        ],
                        width: { size: 60, type: WidthType.PERCENTAGE },
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: review.source,
                                size: 9,
                                color: "0066CC",
                              }),
                              new TextRun({
                                text: `\n${review.category}`,
                                size: 8,
                                color: "999999",
                              }),
                            ],
                            alignment: AlignmentType.CENTER,
                          }),
                        ],
                        width: { size: 15, type: WidthType.PERCENTAGE },
                      }),
                    ],
                  });
                }),
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                columnWidths: [1500, 6500, 1500],
              }),
            ],
            footers: {
              default: new Footer({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Confidential - Generated by ${tenant?.name}`,
                        size: 10,
                        color: "888888",
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                  }),
                  ...(logoImageData
                    ? [
                        new Paragraph({
                          children: [
                            new ImageRun({
                              data: logoImageData.data,
                              type: logoImageData.mimeType,
                              transformation: { width: 60, height: 60 },
                            }),
                          ],
                          alignment: AlignmentType.RIGHT,
                          spacing: { before: 100 },
                        }),
                      ]
                    : []),
                  new Paragraph({
                    text: "",
                    spacing: { after: 100 },
                  }),
                ],
              }),
            },
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `client-reviews-${tenantId}-${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl max-w-4xl m-auto mt-0 2xl:mt-20 overflow-y-auto max-h-[70vh] shadow-sm border border-gray-100 mb-30 animate-pulse">
        {/* Header skeleton */}
        <div className="p-4 bg-[#FAFBFD] rounded-t-2xl">
          {/* Filter Buttons */}
          <div className="flex gap-2 flex-wrap pb-2 px-4">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="h-6 w-28 bg-gray-200 rounded-full" />
            ))}
          </div>

          {/* Sentiment Summary */}
          <div className="grid p-6 grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 mb-6">
            {[...Array(2)].map((_, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-300 rounded-sm" />
                  <div className="h-4 w-32 bg-gray-200 rounded" />
                </div>
                <div className="h-3 w-full bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Review List Skeleton */}
        <div className="space-y-6 p-6">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="pt-4 flex gap-4">
              <div className="min-w-[150px]">
                <div className="h-3 w-24 bg-gray-200 rounded mb-1" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-28 bg-gray-300 rounded" />
                <div className="h-3 w-40 bg-gray-200 rounded" />
                <div className="h-4 w-full bg-gray-100 rounded" />
                <div className="flex gap-2 mt-2">
                  <div className="h-6 w-20 bg-gray-200 rounded-full" />
                  <div className="h-6 w-16 bg-gray-100 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data?.content) {
    return <div className="text-red-500 text-sm">Failed to load data.</div>;
  }

  const { reviews } = data.content;
  const filteredReviews = activeCategory
    ? reviews.filter((r) =>
        r.category.toLowerCase().includes(activeCategory.toLowerCase())
      )
    : reviews;

  // Calculate sentiment percentages
  const totalReviews = reviews.length;

  const positiveReviews = reviews.filter((r) => {
    const rating = getNumericRating(r.star_rating);
    return rating !== null && rating >= 3;
  }).length;

  const negativeReviews = reviews.filter((r) => {
    const rating = getNumericRating(r.star_rating);
    return rating !== null && rating <= 2;
  }).length;

  const positivePercentage =
    totalReviews > 0 ? (positiveReviews / totalReviews) * 100 : 0;
  const negativePercentage =
    totalReviews > 0 ? (negativeReviews / totalReviews) * 100 : 0;

  // Get unique categories for filtering
  const categories = [...new Set(reviews.map((r) => r.category))];

  return (
    <div className="bg-[#FDFDFD] rounded-xl max-w-4xl m-auto mt-0 2xl:mt-20  shadow-sm border border-gray-100 mb-30">
      <div className="p-4 bg-[#FAFBFD] rounded-t-2xl">
        {/* Filter Categories */}
           <div className="flex gap-2 mb-2 justify-end">
                  <button
                    onClick={downloadPDF}
                    disabled={downloadingPdf}
                    className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {downloadingPdf ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <RiDownloadLine className="w-4 h-4" /> PDF
                      </>
                    )}
                  </button>
        
                  <button
                    onClick={downloadWord}
                    disabled={downloadingWord}
                    className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {downloadingWord ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <RiDownloadLine className="w-4 h-4" />
                        Word
                      </>
                    )}
                  </button>
                </div>
        <div className="flex gap-2 flex-wrap pb-2 px-4">
          <button
            onClick={() => setActiveCategory(null)}
            className={`border border-gray-300 px-3 py-1 rounded-full text-sm cursor-pointer ${
              !activeCategory
                ? "bg-gray-50 text-blue-600"
                : "text-gray-700 bg-white"
            }`}
          >
            All Reviews ({totalReviews})
          </button>
          {positivePercentage > 0 && (
            <span className="border border-gray-300 px-3 py-1 rounded-full text-sm text-gray-700 bg-white">
              Positive ({Math.round(positivePercentage)}%)
            </span>
          )}
          {negativePercentage > 0 && (
            <span className="border border-gray-300 px-3 py-1 rounded-full text-sm text-gray-700 bg-white">
              Negative ({Math.round(negativePercentage)}%)
            </span>
          )}

          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`border border-gray-300 px-3 py-1 rounded-full text-sm cursor-pointer ${
                activeCategory === category
                  ? "bg-gray-100 text-blue-700"
                  : "text-gray-700 bg-white hover:bg-gray-50"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Sentiment Summary */}
        <div className="grid p-6 grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 mb-6">
          {positivePercentage > 0 && (
            <div className="gap-2 items-center">
              <span className="text-black font-medium flex items-center">
                🟩{" "}
                {Number.isInteger(positivePercentage)
                  ? positivePercentage
                  : Math.round(positivePercentage)}
                % Positive
              </span>
              <p>Customers praised product quality and company standards.</p>
            </div>
          )}
          {negativePercentage > 0 && (
            <div className="gap-2 items-center">
              <span className="text-black  font-medium flex items-center">
                🟥{" "}
                {Number.isInteger(negativePercentage)
                  ? negativePercentage
                  : Math.round(negativePercentage)}
                % Negative
              </span>
              <p>Concerns around documentation and workload management.</p>
            </div>
          )}
        </div>
      </div>

      {/* Review List */}
      <div className="space-y-6 p-6">
        {filteredReviews.map((review, idx) => {
          const rating = getNumericRating(review.star_rating);
          return (
            <div key={idx} className="pt-4 flex">
              <p className="text-sm text-gray-500 mb-1 min-w-[150px]">
                {review.date_of_review
                  ? new Date(review.date_of_review).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : "No date"}
              </p>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-md ${
                      rating && rating <= 2
                        ? "text-red-500 font-light"
                        : "text-gray-600"
                    }`}
                  >
                    {review.star_rating
                      ? `Rating ${review.star_rating}`
                      : "No rating"}
                  </span>
                </div>
                {/* <h3 className="text-gray-600 font-light block mb-1">
                  {review.reviewer_designation}
                </h3> */}
                <p className="text-gray-600/95 mb-2">
                  {review.review_text || review.summary}
                </p>
                <p className="text-xs text-gray-500 flex gap-2 items-center flex-wrap">
                  <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                    {review.source}
                  </span>
                  <span className="text-gray-500">#{review.category}</span>
                </p>
              </div>
            </div>
          );
        })}

        {/* Citations Section */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Sources</h3>
          <div className="flex gap-2 flex-wrap">
            {data.citations.map((url, idx) => (
              <button
                key={idx}
                onClick={(e) => handleSourceClick(e, url)}
                className="border border-gray-300 px-3 py-1 rounded-full text-sm text-gray-700 hover:bg-gray-50 transition cursor-pointer"
              >
                {new URL(url).hostname}
              </button>
            ))}
          </div>
        </div>
      </div>
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
